import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

export const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("tce_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const formatApiError = (e, fallback = "Something went wrong. Please try again.") => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || "").filter(Boolean).join(" ") || fallback;
  return detail?.msg || fallback;
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined=loading, null=guest

  const loadMe = useCallback(async () => {
    if (!localStorage.getItem("tce_token")) { setUser(null); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      localStorage.removeItem("tce_token");
      setUser(null);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const startSession = (token, userObj) => {
    localStorage.setItem("tce_token", token);
    setUser(userObj);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    startSession(data.token, data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("tce_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, startSession, reload: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
